import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowUpRight } from 'lucide-react';
import { hero, SITE, stats } from '../data/mock';
import HeroMusic from './HeroMusic';
import HeroVideo from './HeroVideo';
import { isVideoUrl } from '../utils/mediaType';
import { fitToClass } from '../utils/mediaFit';
import { useCopy } from '../context/SiteCopyContext';
import { useSiteMedia } from '../utils/useSiteMedia';

/**
 * AdminHero — admin-managed hero slideshow.
 *
 * Reads slot `hero` from /api/site-media and renders curated media (images +
 * videos) with the chosen transition. Falls back to the built-in <HeroVideo>
 * when no media has been uploaded yet.
 */
const AdminHero = () => {
  const cfg = useSiteMedia('hero');
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  const heroSubtitle = useCopy('home.hero.subtitle', 'Solid timber. Fine fabrics. Furniture built quietly in Australia — designed to sit in your home for decades, not seasons.');
  const ctaPrimary = useCopy('home.hero.cta_primary', 'Shop the collection');
  const ctaSecondary = useCopy('home.hero.cta_secondary', 'Our story');

  const media = useMemo(() => cfg?.media || [], [cfg?.media]);
  const transition = cfg?.transition || 'fade';
  const slideMs = cfg?.duration_ms || 5000;
  const transitionMs = cfg?.transition_ms || 900;
  const total = media.length;

  useEffect(() => {
    if (total <= 1) return undefined;
    const current = media[active];
    if (current?.kind === 'video' || (current && isVideoUrl(current.url))) return undefined;
    timerRef.current = window.setTimeout(() => setActive((i) => (i + 1) % total), slideMs);
    return () => window.clearTimeout(timerRef.current);
  }, [active, total, slideMs, media]);

  if (cfg === null) return null;
  if (total === 0) return <HeroVideo />;
  const titleWords = (hero?.titleItalic || 'thread').toString();
  const before = (hero?.titleBefore || 'Where timber meets').toString();
  const trailing = (hero?.titleAfter || '.').toString();

  return (
    <section className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#0F0A06]" data-testid="admin-hero">
      {media.map((m, i) => {
        const isActive = i === active;
        const next = (active + 1) % total;
        const isNext = i === next;
        if (!isActive && !isNext && total > 2) return null;
        const isVid = m.kind === 'video' || isVideoUrl(m.url);
        let stateClass = '';
        let styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${transitionMs + 400}ms ease` };
        if (transition === 'fade' || transition === 'dissolve') {
          stateClass = isActive ? 'opacity-100' : 'opacity-0';
        } else if (transition === 'slide') {
          stateClass = isActive ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12';
          styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${transitionMs}ms ease` };
        } else if (transition === 'zoom') {
          stateClass = isActive ? 'opacity-100 scale-[1.08]' : 'opacity-0 scale-100';
          styleExtra = { transition: `opacity ${transitionMs}ms ease, transform ${slideMs}ms ease-out` };
        }
        return (
          <div key={`${m.url}-${i}`} className={`absolute inset-0 ${stateClass}`} style={styleExtra}>
            {isVid ? (
              <video
                ref={isActive ? videoRef : undefined}
                src={m.url}
                autoPlay={isActive}
                muted
                loop={total === 1}
                playsInline
                onEnded={() => { if (total > 1 && isActive) setActive((j) => (j + 1) % total); }}
                className={`w-full h-full ${fitToClass(m.fit)}`}
              />
            ) : (
              <img src={m.url} alt={m.alt || ''} className={`w-full h-full ${fitToClass(m.fit)}`} />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/55" />
          </div>
        );
      })}

      {total > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20" data-testid="hero-dots">
          {media.map((_, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`hero-dot-${i}`}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === active ? 'w-9 bg-[#F5EDDD]' : 'w-1.5 bg-[#F5EDDD]/40 hover:bg-[#F5EDDD]/70'}`}
            />
          ))}
        </div>
      )}

      <div className="relative z-10 h-full flex items-end pb-24 md:pb-28">
        <div className="max-w-[1480px] w-full mx-auto px-6 md:px-10">
          <p className="text-[10px] tracking-[0.42em] uppercase text-[#E3D5B8] mb-5 animate-[fadeUp_1100ms_ease-out_60ms_both]">
            — {SITE?.tagline || hero?.eyebrow || 'Made in Australia · Limited release'}
          </p>
          <h1
            className="text-[#F5EDDD] leading-[1.02] tracking-[-0.01em] text-[clamp(56px,9vw,164px)] animate-[fadeUp_1100ms_ease-out_120ms_both]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            <span>{before} </span>
            <span className="italic" style={{ color: '#E3D5B8' }}>{titleWords}</span>
            <span>{trailing}</span>
          </h1>
          <p className="mt-7 max-w-xl text-[15px] sm:text-[16px] leading-[1.8] text-[#F5EDDD]/85 animate-[fadeUp_1100ms_ease-out_220ms_both]">
            {heroSubtitle}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4 animate-[fadeUp_1100ms_ease-out_420ms_both]">
            <Button asChild className="bg-[#F5EDDD] text-[#33200F] hover:bg-[#FBF6EC] rounded-none h-12 px-9 text-[12px] tracking-[0.22em] uppercase">
              <Link to="/collections">{ctaPrimary}</Link>
            </Button>
            <Button asChild variant="outline" className="bg-transparent border border-[#F5EDDD]/70 text-[#F5EDDD] hover:bg-[#F5EDDD] hover:text-[#33200F] rounded-none h-12 px-7 text-[12px] tracking-[0.22em] uppercase">
              <Link to="/about">{ctaSecondary} <ArrowUpRight className="ml-2 h-3.5 w-3.5" /></Link>
            </Button>
          </div>
          {stats && stats.length > 0 && (
            <ul className="mt-14 grid grid-cols-3 gap-6 max-w-2xl animate-[fadeUp_1100ms_ease-out_620ms_both]">
              {stats.map((s) => (
                <li key={s.label} className="border-l border-[#F5EDDD]/30 pl-4">
                  <p className="text-[#F5EDDD] text-3xl md:text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.value}</p>
                  <p className="text-[10px] tracking-[0.32em] uppercase text-[#E3D5B8] mt-1">{s.label}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <HeroMusic />
    </section>
  );
};

export default AdminHero;
